import React from 'react';

/**
 * Terroir atoms - low-level building blocks dla redesignu.
 * Wszystkie style inline + CSS variables z .redesign-root scope.
 *
 * Nie używają Tailwinda celowo - żeby paleta była niezależna od
 * tailwind.config.js produkcji i żeby ten katalog można było wyciągnąć
 * jednym ruchem jeśli decyzja będzie negatywna.
 */

// ── Page title (editorial - Fraunces 44px light) ──────────────────
export function PageTitle({ eyebrow, title, sub, kicker }) {
  return (
    <div style={{ padding: '8px 24px 18px' }}>
      {eyebrow && (
        <div style={{
          fontFamily: 'var(--t-mono)',
          fontSize: 10,
          letterSpacing: '.22em',
          textTransform: 'uppercase',
          color: 'var(--t-text-dim)',
          marginBottom: 10,
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span>{eyebrow}</span>
          {kicker && <span>{kicker}</span>}
        </div>
      )}
      <h1 className="t-dropcap" style={{
        margin: 0,
        fontFamily: 'var(--t-display)',
        fontWeight: 300,
        fontSize: 'clamp(40px, 8vw, 56px)',
        lineHeight: 0.95,
        letterSpacing: '-.02em',
        color: 'var(--t-text)',
        textWrap: 'balance',
      }}>{title}</h1>
      {sub && (
        <p style={{
          margin: '12px 0 0',
          fontSize: 14,
          lineHeight: 1.45,
          color: 'var(--t-text-dim)',
          maxWidth: 420,
        }}>{sub}</p>
      )}
    </div>
  );
}

// ── Rule (cienka linia z opcjonalną etykietą po środku) ──────────
export function Rule({ label }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '0 24px',
      margin: '22px 0 14px',
    }}>
      <div style={{ flex: 1, height: 1, background: 'var(--t-line-2)' }} />
      {label && (
        <span style={{
          fontFamily: 'var(--t-mono)',
          fontSize: 10,
          letterSpacing: '.22em',
          textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>{label}</span>
      )}
      <div style={{ flex: 1, height: 1, background: 'var(--t-line-2)' }} />
    </div>
  );
}

// ── Chip (pill z kolorem tonu) ────────────────────────────────────
export function Chip({ children, tone = 'dim', selected, onClick, title, ariaLabel, style }) {
  const tones = {
    sage:  { bg: 'var(--t-sage-bg)',  fg: 'var(--t-sage)',  bo: 'var(--t-sage-bo)' },
    amber: { bg: 'var(--t-amber-bg)', fg: 'var(--t-amber)', bo: 'var(--t-amber-bo)' },
    rust:  { bg: 'var(--t-rust-bg)',  fg: 'var(--t-rust)',  bo: 'var(--t-rust-bo)' },
    dim:   { bg: 'var(--t-dim-bg)',   fg: 'var(--t-text-dim)', bo: 'var(--t-line-2)' },
  };
  const t = tones[tone] || tones.dim;
  const sel = selected ? {
    background: 'var(--t-sage)',
    color: 'var(--t-bg)',
    borderColor: 'var(--t-sage)',
  } : {};
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    borderRadius: 999,
    fontFamily: 'var(--t-mono)',
    fontSize: 10,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    fontWeight: 500,
    background: t.bg,
    color: t.fg,
    border: `1px solid ${t.bo}`,
    whiteSpace: 'nowrap',
    ...sel,
  };
  if (typeof onClick === 'function') {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={ariaLabel}
        aria-pressed={selected ? 'true' : 'false'}
        style={{
          ...baseStyle,
          cursor: 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          transition: 'transform 120ms ease, background 120ms ease',
          ...style,
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >{children}</button>
    );
  }
  return (
    <span style={{ ...baseStyle, ...style }}>{children}</span>
  );
}

// ── Button (3 warianty: primary sage, amber, ghost) ──────────────
// Button: jeśli bloomOnClick=true (domyślnie dla primary/amber), kliknięcie
// emituje subtelny bloom-burst za przyciskiem (radialny rozbłysk sage) — UI celebrates.
export function Button({ children, variant = 'primary', full, icon, onClick, type = 'button', bloomOnClick }) {
  const [bursts, setBursts] = React.useState([]);
  const enableBloom = bloomOnClick !== false && variant !== 'ghost';

  const handleClick = (e) => {
    if (enableBloom) {
      const id = Date.now() + Math.random();
      setBursts(prev => [...prev, id]);
      setTimeout(() => setBursts(prev => prev.filter(b => b !== id)), 700);
    }
    onClick?.(e);
  };

  const base = {
    height: 52,
    borderRadius: 'var(--t-radius)',
    border: 'none',
    fontFamily: 'var(--t-sans)',
    fontSize: 15,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '0 20px',
    cursor: 'pointer',
    transition: 'transform 120ms ease, opacity 120ms ease',
    width: full ? '100%' : undefined,
    position: 'relative',
    overflow: 'hidden',
  };
  const variants = {
    primary: { background: 'var(--t-sage)',  color: 'var(--t-bg)' },
    amber:   { background: 'var(--t-amber)', color: 'var(--t-bg)' },
    ghost:   { background: 'transparent', color: 'var(--t-text)', border: '1px solid var(--t-line-2)' },
  };
  return (
    <button
      type={type}
      onClick={handleClick}
      style={{ ...base, ...(variants[variant] || variants.primary) }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {icon}{children}
      {bursts.map(id => (
        <span key={id} className="t-bloom-burst" aria-hidden="true" />
      ))}
    </button>
  );
}

// ── Card (surface elevation z cienką linią) ──────────────────────
export function Card({ children, padding = 16, style, ...rest }) {
  return (
    <div
      {...rest}
      style={{
        padding,
        borderRadius: 'var(--t-radius-lg)',
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        ...style,
      }}
    >{children}</div>
  );
}

// ── Meta line (uppercase mono dla etykiet sekcji) ────────────────
export function Meta({ children, color = 'dim', as = 'div', style, ...rest }) {
  const colors = {
    dim:  'var(--t-text-dim)',
    mute: 'var(--t-text-mute)',
    sage: 'var(--t-sage)',
    amber: 'var(--t-amber)',
    rust: 'var(--t-rust)',
  };
  const Tag = as;
  return (
    <Tag {...rest} style={{
      fontFamily: 'var(--t-mono)',
      fontSize: 10,
      letterSpacing: '.22em',
      textTransform: 'uppercase',
      color: colors[color] || colors.dim,
      fontWeight: 500,
      ...style,
    }}>{children}</Tag>
  );
}

// ── Display number (Fraunces light, dla d24, 18°, etc) ───────────
// Display: jeśli dropcap=true i children to string, pierwsza litera
// dostaje botaniczną oprawę z tokens.css (.t-dropcap::first-letter).
export function Display({ children, size = 18, style, dropcap = false, className, ...rest }) {
  const cls = [className, dropcap && size >= 28 ? 't-dropcap' : null].filter(Boolean).join(' ') || undefined;
  return (
    <span {...rest} className={cls} style={{
      fontFamily: 'var(--t-display)',
      fontSize: size,
      fontWeight: 300,
      color: 'var(--t-text)',
      lineHeight: 1,
      letterSpacing: '-.01em',
      ...style,
    }}>{children}</span>
  );
}

// ── SVG icons (lokalne, żeby nie dodawać deps) ───────────────────
export const Icons = {
  Drop: ({ color = 'var(--t-sage)', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 2l4 5.5a4.5 4.5 0 11-8 0L8 2z" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
  Sun: ({ color = 'var(--t-amber)', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3" stroke={color} strokeWidth="1.4" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M13 3l-1.5 1.5M4.5 11.5L3 13"
            stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Sprout: ({ color = 'var(--t-sage)', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 14V8M8 8C8 5 6 3 3 3c0 3 2 5 5 5zM8 8c0-2 1.5-3.5 4-3.5C12 6.5 10.5 8 8 8z"
            stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Scissors: ({ color = 'var(--t-rust)', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="4" cy="12" r="2" stroke={color} strokeWidth="1.4" />
      <circle cx="12" cy="12" r="2" stroke={color} strokeWidth="1.4" />
      <path d="M5.5 10.5L14 2M10.5 10.5L2 2" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Today: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7" stroke={color} strokeWidth="1.4" />
      <path d="M10 6v4l2.5 2" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Plants: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M10 17V9M10 9C10 6 8 4 5 4c0 3 2 5 5 5zM10 9c0-2.5 1.5-4 4-4 0 2.5-1.5 4-4 4z"
            stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Plot: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="3" width="14" height="14" rx="1" stroke={color} strokeWidth="1.4" />
      <path d="M3 8h14M3 13h14M8 3v14M13 3v14" stroke={color} strokeWidth="1.4" strokeOpacity=".5" />
    </svg>
  ),
  Calendar: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="5" width="14" height="12" rx="1.5" stroke={color} strokeWidth="1.4" />
      <path d="M3 8h14M7 3v4M13 3v4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Journal: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M5 3h9l2 2v12H5V3z" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 8h6M8 11h6M8 14h4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Menu: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M3 6h14M3 10h14M3 14h14" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  Search: ({ color = 'currentColor', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5" stroke={color} strokeWidth="1.5" />
      <path d="M11 11l3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Flame: ({ color = 'var(--t-amber)', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 2c1.5 2 3 3.5 3 6a3 3 0 11-6 0c0-1.5 1-3 3-6z" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 8c.5 1 1 1.5 1 2.5a1 1 0 11-2 0c0-.5.5-1.5 1-2.5z" fill={color} opacity="0.5" />
    </svg>
  ),
  Bloom: ({ color = 'var(--t-sage)', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2" stroke={color} strokeWidth="1.4" />
      <path d="M8 2v3M8 11v3M2 8h3M11 8h3M4 4l2 2M10 10l2 2M12 4l-2 2M6 10l-2 2" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Friends: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="7.5" cy="7" r="2.6" stroke={color} strokeWidth="1.4" />
      <path d="M3 16c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M13 6.2a2.4 2.4 0 013.4 3M14 12.2c2 .3 3.4 1.7 3.4 3.8" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Exchange: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M4 8h11l-3-3M16 12H5l3 3" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Community: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="8" cy="8" r="2.5" stroke={color} strokeWidth="1.4" />
      <circle cx="14" cy="10" r="2.5" stroke={color} strokeWidth="1.4" />
      <path d="M3 16c0-3 2.5-4.5 5-4.5s5 1.5 5 4.5M10 16c0-1.5 1-2.5 2.5-2.5s2.5 1 2.5 2.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  More: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="5" cy="10" r="1.8" fill={color} />
      <circle cx="10" cy="10" r="1.8" fill={color} />
      <circle cx="15" cy="10" r="1.8" fill={color} />
    </svg>
  ),
  Shield: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <path d="M10 3l6 2v5c0 4-2.5 7-6 9-3.5-2-6-5-6-9V5l6-2z" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M7.5 10l2 2 3-4" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Help: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7" stroke={color} strokeWidth="1.4" />
      <path d="M8 8.5a2 2 0 113.5 1.5c-1 .8-1.5 1.5-1.5 2.5M10 15.5a.75.75 0 100-1.5.75.75 0 000 1.5z" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  Back: ({ color = 'currentColor', size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M12.5 15L7.5 10L12.5 5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};
